import React, { useState, useRef, Fragment, useEffect } from 'react';
import { useNavigation } from 'context/NavigationContext';
import { useParams } from 'react-router-dom';
import { BringzzPageContainer } from '@bringzz/components';
import { useTheme } from 'context/ThemeContext';
import { useBasket } from 'context/BasketContext';
import { useOrder } from 'context/OrderContext';
import { SCAN_OPTIONS } from 'utils/constants';
import 'react-toastify/dist/ReactToastify.css';

// Components
import CameraStream from 'components/CameraStream';
import CameraModeOverlay from 'components/CameraModeOverlay';
import CameraControlPanel from 'components/CameraControlPanel';
import LoadingOverlay from './components/shared/LoadingOverlay';
import ScanHeader from './components/shared/ScanHeader';
import ProductSelectionModal from './components/PrescriptionScanner/ProductSelectionModal';

// Hooks
import useCameraControl from './hooks/useCameraControl';
import usePrescriptionScanning from './hooks/usePrescriptionScanning';
import useQRScanning from './hooks/useQRScanning';

// Main Camera Page Component
const ScanPage = () => {
    const { backPress, navigate } = useNavigation();
    const { isMobile } = useTheme();
    const { addToBasket } = useBasket();
    const { savePrescriptionInfo, setPrescriptionId, addPrescriptionProductId } = useOrder();

    const { mode, type } = useParams(); // type: pickup/collect, mode: collect/confirm/handover
    const [selectedMode, setSelectedMode] = useState(mode ? mode : 'handover');
    const [selectedOption, setSelectedOption] = useState(SCAN_OPTIONS.QR_CODE);
    const [detectedText, setDetectedText] = useState('');
    const [hasMultipleCameras, setHasMultipleCameras] = useState(true); // Default to true to show button until checked
    const [isFrontCamera, setIsFrontCamera] = useState(false); // Track if front camera is active
    const cameraStreamRef = useRef();

    // Camera control hook
    const {
        isUploading,
        setIsUploading,
        previewImage,
        setPreviewImage,
        handleBackPress,
        handleSwitchCamera,
        resetCamera,
        handleImageSelected
    } = useCameraControl(cameraStreamRef, backPress);

    // Prescription scanning hook
    const {
        prescriptionData,
        showProductSelectionModal,
        selectedProducts,
        processingProducts,
        handlePrescriptionUpload,
        handleProductSelection,
        handleConfirmProductSelection,
        handleCancelProductSelection,
        resetPrescriptionState
    } = usePrescriptionScanning(addToBasket, savePrescriptionInfo, setPrescriptionId, addPrescriptionProductId, navigate);

    // QR scanning hook
    const {
        handleQRCodeUpload
    } = useQRScanning();

    // Handle camera detection change from CameraStream
    const handleCameraDetectionChange = (hasMultiple) => {
        console.log('[ScanPage] Camera detection changed:', hasMultiple);
        setHasMultipleCameras(hasMultiple);
    };

    // Handle facing mode change from CameraStream
    const handleFacingModeChange = (facingMode) => {
        console.log('[ScanPage] Facing mode changed:', facingMode);
        setIsFrontCamera(facingMode === 'user');
    };

    // Cleanup: Stop camera when component unmounts
    useEffect(() => {
        console.log('[ScanPage] Component mounted');
        return () => {
            console.log('[ScanPage] Component unmounting - cleanup triggered');
            try {
                if (cameraStreamRef.current) {
                    console.log('[ScanPage] Calling stopCamera on unmount');
                    cameraStreamRef.current.stopCamera();
                } else {
                    console.log('[ScanPage] No camera ref on unmount');
                }
            } catch (error) {
                console.error('[ScanPage] Error stopping camera on unmount:', error);
            }
            console.log('[ScanPage] Unmount cleanup complete');
        };
    }, []);

    const handleDetectValue = (result) => {
        if (result && result.text) {
            console.log(result.text);
            setDetectedText(result.text);
        }
    };

    const handleImageUpload = async (imageFile) => {
        if (selectedOption === SCAN_OPTIONS.QR_CODE) {
            await handleQRCodeUpload(imageFile, setIsUploading, setPreviewImage, resetCamera);
        } else {
            const success = await handlePrescriptionUpload(imageFile, setIsUploading, setPreviewImage);
            if (!success) {
                resetCamera();
            }
        }
    };

    const onCancelProductSelection = () => {
        handleCancelProductSelection();
        resetCamera();
    };

    return (
        <Fragment>
            <BringzzPageContainer authenticated={false} specialHeader={true} className={`h-screen overflow-hidden ${isMobile ? 'fixed inset-0' : ''}`}>
                <div className={`relative w-full h-screen max-w-4xl mx-auto overflow-hidden ${isMobile ? 'touch-none' : ''}`}>
                    {/* Show either preview image or camera stream */}
                    <div className="w-full h-full">
                        {previewImage ? (
                            <div className="h-full w-full">
                                <img
                                    src={previewImage}
                                    alt="Preview"
                                    className="h-full w-full object-cover"
                                />
                            </div>
                        ) : (
                            <CameraStream
                                ref={cameraStreamRef}
                                setDetectedText={handleDetectValue}
                                onCameraDetectionChange={handleCameraDetectionChange}
                                onFacingModeChange={handleFacingModeChange}
                            />
                        )}

                        {/* Mode Overlay */}
                        <CameraModeOverlay mode={selectedMode} selectedOption={selectedOption} text={detectedText} />

                        {/* Loading Overlay */}
                        {isUploading && <LoadingOverlay />}

                        {/* Top-left content area */}
                        <ScanHeader
                            onBackPress={handleBackPress}
                            onFlashToggle={() => {
                                if (cameraStreamRef.current) {
                                    cameraStreamRef.current.toggleTorch();
                                }
                            }}
                            isMobile={isMobile}
                            isFrontCamera={isFrontCamera}
                        />

                        {/* Bottom-center control area */}
                        <div className="absolute left-0 right-0 z-10" style={{ bottom: 0 }}>
                            <CameraControlPanel
                                mode={mode}
                                selectedOption={selectedOption}
                                setSelectedOption={setSelectedOption}
                                onSwitchCamera={handleSwitchCamera}
                                onImageSelected={(input) => handleImageSelected(input, handleImageUpload)}
                                navigate={navigate}
                                isDisabled={isUploading}
                                hasMultipleCameras={hasMultipleCameras}
                            />
                        </div>
                    </div>

                    {/* Product Selection Modal */}
                    {showProductSelectionModal && (
                        <ProductSelectionModal
                            isOpen={showProductSelectionModal}
                            prescriptionData={prescriptionData}
                            selectedProducts={selectedProducts}
                            processingProducts={processingProducts}
                            onProductSelection={handleProductSelection}
                            onConfirm={handleConfirmProductSelection}
                            onCancel={onCancelProductSelection}
                        />
                    )}
                </div>
            </BringzzPageContainer>
        </Fragment>
    );
};

export default ScanPage;
